import React, { CSSProperties } from "react";
import { DraggableData, Position, Rnd } from "react-rnd";
import { TTemplate, TCropCoords } from "../../../../../utils/types";
import { mixpanel } from "../../../../../utils/mixpanel";
import { SessionContext } from "../../../../../contexts";
import { MAX_ZINDEX, TOOLTIP_STEPS } from "../../../../../utils/constant";
import AddTextModalContent from "./add-text-modal-content/AddTextModalContent";
import AddImageModalContent from "./add-image-modal-content/AddImageModalContent";
import { View, StyleSheet } from "react-native";
import OpenModalButtons from "./OpenModalButtons";
import AdjustTemplateContent from "./adjust-template-content/AdjustTemplateContent";
import SwapImageModalContent from "./swap-image/SwapImageModalContent";
import { RainbowBorder } from "../../../../common/native";
import { useOnboardingTips } from "../../../../../contexts/OnboardingTipsContext";
import TemplateSettingsModal from "./TemplateSettingsModal";
import ManualPairingPanel from "../step4/ManualPairingPanel";
import { TSideBySideProcessedImage } from "../../../../../services/api-clinic";
import useSelectedLayerKeyboardShortcuts from "../../../../common/web/useSelectedLayerKeyboardShortcuts.web";

const AFTER_PLACEHOLDER_IMAGE_SRC =
  "https://clinic-os.com/user-app-assets/before-after-generator-after-image-placeholder.png";
const BEFORE_PLACEHOLDER_IMAGE_SRC =
  "https://clinic-os.com/user-app-assets/before-after-generator-before-image-placeholder.png";

type TemplateProps = {
  template: TTemplate;
  displayMode: boolean;
  afterImage?: string;
  beforeImage?: string;
  beforeCropCoords?: TCropCoords;
  setTemplate?: (template: TTemplate) => void;
  afterCropCoords?: TCropCoords;
  swapImagesModalData: {
    handleSetAfterCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords?: TCropCoords,
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBeforeCroppedImage: (
      image: string,
      index: number,
      updatedCropCoords?: TCropCoords,
      selectedImage?: TSideBySideProcessedImage,
      rotationDegrees?: number,
    ) => void;
    handleSetBothCroppedImages: (
      beforeImage: string,
      afterImage: string,
      index: number,
      beforeCropCoords: TCropCoords,
      afterCropCoords: TCropCoords,
      beforeRotationDegrees?: number,
      afterRotationDegrees?: number,
    ) => void;
    templatesData: any[];
    currentTemplateIndex: number;
    uploadedImages: { preOp: Blob[]; postOp: Blob[] };
    processedImageUrls: { preOp: string[]; postOp: string[] };
    beforeImageOptions: TSideBySideProcessedImage[];
    afterImageOptions: TSideBySideProcessedImage[];
    unpairedBeforeImages?: TSideBySideProcessedImage[];
    unpairedAfterImages?: TSideBySideProcessedImage[];
    onPairUnpairedImages?: (
      beforeImage: TSideBySideProcessedImage,
      afterImage: TSideBySideProcessedImage,
    ) => void;
  } | null;
  tipsEnabled?: boolean;
};

const Template: React.FC<TemplateProps> = ({
  template,
  displayMode,
  setTemplate = () => {},
  afterImage = AFTER_PLACEHOLDER_IMAGE_SRC,
  beforeImage = BEFORE_PLACEHOLDER_IMAGE_SRC,
  beforeCropCoords,
  afterCropCoords,
  swapImagesModalData,
  tipsEnabled = false,
}) => {
  // console.log("🖼️ Template Rendering: ");
  const session = React.useContext(SessionContext)?.session;
  const { tipsState, markStepComplete } = useOnboardingTips();

  const [openModal, setOpenModal] = React.useState<
    | "add_text"
    | "add_image"
    | "adjust_template"
    | "swap_image"
    | "unpaired"
    | null
  >(null);
  const [isBeforeImageSelection, setIsBeforeImageSelection] =
    React.useState(true);
  const [currentTextEditingIndex, setCurrentTextEditingIndex] = React.useState<
    number | null
  >(null);
  const [currentImageEditingIndex, setCurrentImageEditingIndex] =
    React.useState<number | null>(null);
  const [draggingImageIndex, setDraggingImageIndex] = React.useState<
    number | null
  >(null);
  const isDraggingText = React.useRef(false);
  const templateContainerRef = React.useRef<HTMLDivElement | null>(null);
  const scaleWrapperRef = React.useRef<HTMLDivElement | null>(null);
  // The template's coordinate system is authored against the outerContainer's
  // intrinsic width (1000 for full-size templates, or whatever the caller
  // overrides for thumbnail previews). The editor visually fits the template
  // into the available width via CSS transform: scale() so layout — and
  // therefore text wrapping — always resolves against the same parent width
  // the export uses.
  const baseWidth =
    typeof template.outerContainer.width === "number"
      ? template.outerContainer.width
      : 1000;
  const baseHeight =
    typeof template.outerContainer.height === "number"
      ? template.outerContainer.height
      : 1000;
  const [editorScale, setEditorScale] = React.useState(1);

  React.useLayoutEffect(() => {
    const el = scaleWrapperRef.current;
    const parent = el?.parentElement;
    if (!el || !parent) return;
    const update = () => {
      const available = parent.clientWidth;
      if (available > 0) {
        // Never upscale — only shrink when the parent is narrower than base.
        setEditorScale(Math.min(1, available / baseWidth));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(parent);
    return () => ro.disconnect();
  }, [baseWidth]);

  const [validBeforeImageUrl, setValidBeforeImageUrl] = React.useState<
    string | null
  >(null);
  const [validAfterImageUrl, setValidAfterImageUrl] = React.useState<
    string | null
  >(null);

  const checkImageExists = (url: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = (e) => {
        console.error("❌ Image failed to load:", url, e);
        resolve(false);
      };
      img.src = url;
    });
  };

  React.useEffect(() => {
    let revokedBefore: string | null = null;
    let revokedAfter: string | null = null;

    const checkUrls = async () => {
      const beforeIsValid = await checkImageExists(beforeImage);
      const afterIsValid = await checkImageExists(afterImage);

      if (beforeIsValid) {
        setValidBeforeImageUrl(beforeImage);
      } else if (swapImagesModalData?.uploadedImages?.preOp?.length) {
        const blob = swapImagesModalData.uploadedImages.preOp[0];
        const fallback = URL.createObjectURL(blob);
        revokedBefore = fallback;
        setValidBeforeImageUrl(fallback);
      }

      if (afterIsValid) {
        setValidAfterImageUrl(afterImage);
      } else if (swapImagesModalData?.uploadedImages?.postOp?.length) {
        const blob = swapImagesModalData.uploadedImages.postOp[0];
        const fallback = URL.createObjectURL(blob);
        revokedAfter = fallback;
        setValidAfterImageUrl(fallback);
      }
    };

    checkUrls();

    return () => {
      if (revokedBefore) URL.revokeObjectURL(revokedBefore);
      if (revokedAfter) URL.revokeObjectURL(revokedAfter);
    };
  }, [beforeImage, afterImage]);

  const maybeMarkTemplateEditComplete = () => {
    // Conditionally marks template edit as complete
    // if the tipstate is appropriate
    if (tipsState.editTemplate && !tipsState.addTextOrImages) {
      markStepComplete(TOOLTIP_STEPS.EDIT_TEMPLATE);
    }
  };

  const handleCloseEditMode = React.useCallback(() => {
    setCurrentTextEditingIndex(null);
    setCurrentImageEditingIndex(null);
    setOpenModal(null);
  }, []);

  const handleTextEditingMode = (textIndex: number | null) => {
    setCurrentTextEditingIndex(textIndex);
    setCurrentImageEditingIndex(null);
    setOpenModal("add_text");
    maybeMarkTemplateEditComplete();
  };

  const handleImageEditingMode = (imageIndex: number | null) => {
    setCurrentImageEditingIndex(imageIndex);
    setCurrentTextEditingIndex(null);
    setOpenModal("add_image");
    maybeMarkTemplateEditComplete();
  };

  const handleDeleteText = (index: number) => {
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        texts: prev.texts.filter((_: any, i: number) => i !== index),
      };
    });
    handleTextEditingMode(null);
    handleCloseEditMode();
    maybeMarkTemplateEditComplete();
  };

  const handleDeleteImage = (index: number) => {
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        images: prev.images.filter((_: any, i: number) => i !== index),
      };
    });
    handleImageEditingMode(null);
    handleCloseEditMode();
  };

  const handleDuplicateImage = (index: number) => {
    const DUPLICATE_OFFSET = 12;
    let duplicatedImageIndex: number | null = null;
    const containerWidth = templateContainerRef.current?.clientWidth ?? null;
    const containerHeight = templateContainerRef.current?.clientHeight ?? null;

    const parseSizeToPx = (
      size: string | number,
      containerSize: number | null,
    ): number => {
      if (typeof size === "number") return size;
      if (size.endsWith("%") && containerSize !== null) {
        return (parseFloat(size) / 100) * containerSize;
      }
      return parseFloat(size);
    };

    // @ts-ignore
    setTemplate((prev) => {
      const sourceImage = prev.images[index];
      if (!sourceImage) return prev;

      duplicatedImageIndex = prev.images.length;

      const imageWidth = parseSizeToPx(sourceImage.size.width, containerWidth);
      const imageHeight = parseSizeToPx(
        sourceImage.size.height,
        containerHeight,
      );
      const maxX =
        containerWidth !== null && !Number.isNaN(imageWidth)
          ? Math.max(0, containerWidth - imageWidth)
          : Number.POSITIVE_INFINITY;
      const maxY =
        containerHeight !== null && !Number.isNaN(imageHeight)
          ? Math.max(0, containerHeight - imageHeight)
          : Number.POSITIVE_INFINITY;
      const nextX = Math.min(
        Math.max(0, sourceImage.position.x + DUPLICATE_OFFSET),
        maxX,
      );
      const nextY = Math.min(
        Math.max(0, sourceImage.position.y + DUPLICATE_OFFSET),
        maxY,
      );

      return {
        ...prev,
        images: [
          ...prev.images,
          {
            ...sourceImage,
            position: {
              x: nextX,
              y: nextY,
            },
            size: { ...sourceImage.size },
            containerStyle: { ...sourceImage.containerStyle },
            imageStyle: { ...sourceImage.imageStyle },
            ...(sourceImage.kind === "censorship"
              ? { isUserAdjusted: true }
              : {}),
          },
        ],
      };
    });

    if (duplicatedImageIndex !== null) {
      setCurrentImageEditingIndex(duplicatedImageIndex);
    }

    maybeMarkTemplateEditComplete();
  };

  useSelectedLayerKeyboardShortcuts({
    selectedLayerId: currentImageEditingIndex,
    enabled: !displayMode && openModal === "add_image",
    onDelete: handleDeleteImage,
    onDuplicate: handleDuplicateImage,
  });

  const handleBeforeImageOnDragStop = (d: DraggableData) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        beforeImage: {
          ...prev.beforeImage,
          position: { x: d.x, y: d.y },
        },
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleAfterImageOnDragStop = (d: DraggableData) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        afterImage: {
          ...prev.afterImage,
          position: { x: d.x, y: d.y },
        },
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleBeforeImageResizeStop = (
    ref: HTMLElement,
    position: Position,
  ) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        beforeImage: {
          ...prev.beforeImage,
          size: {
            width: ref.style.width,
            height: ref.style.height,
          },
          position: {
            x: position.x,
            y: position.y,
          },
        },
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleAfterImageResizeStop = (ref: HTMLElement, position: Position) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        afterImage: {
          ...prev.afterImage,
          size: {
            width: ref.style.width,
            height: ref.style.height,
          },
          position: {
            x: position.x,
            y: position.y,
          },
        },
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleImageOnDragStop = (index: number, d: DraggableData) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        images: prev.images.map((img: any, i: number) =>
          i === index
            ? {
                ...img,
                position: { x: d.x, y: d.y },
                ...(img.kind === "censorship" ? { isUserAdjusted: true } : {}),
              }
            : img,
        ),
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleImageResizeStop = (
    index: number,
    ref: HTMLElement,
    position: Position,
  ) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        images: prev.images.map((img: any, i: number) =>
          i === index
            ? {
                ...img,
                size: {
                  width: ref.style.width,
                  height: ref.style.height,
                },
                position: {
                  x: position.x,
                  y: position.y,
                },
                ...(img.kind === "censorship" ? { isUserAdjusted: true } : {}),
              }
            : img,
        ),
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleTextOnDragStop = (index: number, d: DraggableData) => {
    mixpanel.track("Template Edited", {
      "Clinic Name": session?.clinicName ?? "clinic not found",
    });
    // @ts-ignore
    setTemplate((prev) => {
      return {
        ...prev,
        texts: prev.texts.map((txt: any, i: number) =>
          i === index
            ? {
                ...txt,
                position: { x: d.x, y: d.y },
              }
            : txt,
        ),
      };
    });
    maybeMarkTemplateEditComplete();
  };

  const handleImageSelect = (
    imageUrl: string,
    isBefore: boolean,
    selectedImageFromModal?: TSideBySideProcessedImage,
  ) => {
    if (!swapImagesModalData) return;

    const {
      currentTemplateIndex,
      templatesData,
      handleSetBeforeCroppedImage,
      handleSetAfterCroppedImage,
    } = swapImagesModalData;

    const imageOptions = isBefore
      ? swapImagesModalData.beforeImageOptions
      : swapImagesModalData.afterImageOptions;
    const selectedImage =
      selectedImageFromModal ||
      imageOptions.find((image) => image.processed_get_url === imageUrl);
    const cropCoords =
      selectedImage?.cropCoords ||
      (isBefore
        ? templatesData[currentTemplateIndex].beforeCropCoords
        : templatesData[currentTemplateIndex].afterCropCoords);

    if (isBefore) {
      handleSetBeforeCroppedImage(
        imageUrl,
        currentTemplateIndex,
        cropCoords,
        selectedImage,
      );
    } else {
      handleSetAfterCroppedImage(
        imageUrl,
        currentTemplateIndex,
        cropCoords,
        selectedImage,
      );
    }
  };

  const renderModalContent = () => {
    switch (openModal) {
      case "add_text":
        return (
          <AddTextModalContent
            currentTemplate={template}
            setCurrentTemplate={setTemplate}
            currentTextEditingIndex={currentTextEditingIndex}
            onDeleteText={() => {
              const fallbackIndex =
                template.texts && template.texts.length > 0
                  ? template.texts.length - 1
                  : null;
              const targetIndex =
                currentTextEditingIndex !== null
                  ? currentTextEditingIndex
                  : fallbackIndex;
              if (targetIndex === null) return;
              handleDeleteText(targetIndex);
            }}
          />
        );
      case "add_image":
        return (
          <AddImageModalContent
            currentTemplate={template}
            setCurrentTemplate={setTemplate}
            currentImageEditingIndex={currentImageEditingIndex}
            onSetCurrentImageEditingIndex={setCurrentImageEditingIndex}
            onDeleteImage={() => {
              if (currentImageEditingIndex === null) return;
              handleDeleteImage(currentImageEditingIndex);
            }}
            onDuplicateImage={() => {
              if (currentImageEditingIndex === null) return;
              handleDuplicateImage(currentImageEditingIndex);
            }}
          />
        );
      case "adjust_template":
        return (
          swapImagesModalData !== null && (
            <AdjustTemplateContent swapImagesModalData={swapImagesModalData} />
          )
        );
      case "swap_image":
        return (
          swapImagesModalData !== null && (
            <SwapImageModalContent
              isBeforeImageSelection={isBeforeImageSelection}
              setIsBeforeImageSelection={setIsBeforeImageSelection}
              onClose={handleCloseEditMode}
              swapImagesModalData={swapImagesModalData}
              onImageSelect={handleImageSelect}
            />
          )
        );
      case "unpaired":
        return (
          swapImagesModalData !== null && (
            <ManualPairingPanel
              unpairedBeforeImages={
                swapImagesModalData.unpairedBeforeImages || []
              }
              unpairedAfterImages={
                swapImagesModalData.unpairedAfterImages || []
              }
              onPair={(beforeImage, afterImage) => {
                swapImagesModalData.onPairUnpairedImages?.(
                  beforeImage,
                  afterImage,
                );
              }}
            />
          )
        );
      default:
        return null;
    }
  };

  return (
    <div
      style={{
        display: "flex",
        gap: 10,
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        overflow: "visible",
      }}
    >
      <RainbowBorder
        isActive={
          tipsEnabled && tipsState.editTemplate && !tipsState.addTextOrImages
        }
        borderRadius={12}
        borderWidth={5}
        zIndex={10}
      >
        <div
          ref={scaleWrapperRef}
          style={{
            width: baseWidth * editorScale,
            height: baseHeight * editorScale,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              transform: `scale(${editorScale})`,
              transformOrigin: "top left",
              width: baseWidth,
              height: baseHeight,
            }}
          >
            <div
              ref={templateContainerRef}
              style={{
                backgroundColor: "white",
                ...template.outerContainer,
                position: "relative",
                boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
              }}
            >
              {displayMode && (
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    zIndex: MAX_ZINDEX,
                  }}
                />
              )}
              <Rnd
                scale={editorScale}
                bounds="parent"
                minHeight="20%"
                minWidth="20%"
                style={{ ...template.beforeImage.containerStyle }}
                size={{
                  width: template.beforeImage.size.width,
                  height: template.beforeImage.size.height,
                }}
                position={{
                  x: template.beforeImage.position.x,
                  y: template.beforeImage.position.y,
                }}
                onDragStop={(_, d: DraggableData) =>
                  handleBeforeImageOnDragStop(d)
                }
                onResizeStop={(e, direction, ref, delta, position) =>
                  handleBeforeImageResizeStop(ref, position)
                }
              >
                <img
                  // src={beforeImage}
                  src={validBeforeImageUrl || beforeImage}
                  alt="before"
                  style={{
                    ...template.beforeImage.imageStyle,
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </Rnd>
              <Rnd
                scale={editorScale}
                bounds="parent"
                minHeight="20%"
                minWidth="20%"
                style={{ ...template.afterImage.containerStyle }}
                size={{
                  width: template.afterImage.size.width,
                  height: template.afterImage.size.height,
                }}
                position={{
                  x: template.afterImage.position.x,
                  y: template.afterImage.position.y,
                }}
                onDragStop={(_, d: DraggableData) =>
                  handleAfterImageOnDragStop(d)
                }
                onResizeStop={(e, direction, ref, delta, position) =>
                  handleAfterImageResizeStop(ref, position)
                }
              >
                <img
                  // src={afterImage}
                  src={validAfterImageUrl || afterImage}
                  alt="after"
                  style={{
                    ...template.afterImage.imageStyle,
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              </Rnd>

              {template.divs &&
                template.divs.map((element: CSSProperties, index: number) => (
                  <div key={index} style={{ ...element }} />
                ))}

              {currentTextEditingIndex === null ||
                (currentImageEditingIndex === null && (
                  <div
                    onClick={handleCloseEditMode}
                    style={{
                      position: "absolute",
                      inset: 0,
                      zIndex: MAX_ZINDEX - 1,
                    }}
                  />
                ))}

              {template.images &&
                template.images.map(
                  (
                    image: {
                      src: string;
                      preserveAspectRatio?: boolean;
                      kind?: "censorship";
                      censorshipSide?: "before" | "after";
                      regionKey?: string;
                      position: { x: number; y: number };
                      size: { height: string | number; width: string | number };
                      containerStyle: CSSProperties;
                      imageStyle: CSSProperties;
                    },
                    index: number,
                  ) => (
                    <Rnd
                      key={index}
                      scale={editorScale}
                      bounds="parent"
                      minHeight="5%"
                      minWidth="5%"
                      style={{
                        ...image.containerStyle,
                        ...((currentImageEditingIndex === index ||
                          draggingImageIndex === index) && {
                          zIndex: MAX_ZINDEX,
                          outline: "2px dashed #00b7f4",
                          outlineOffset: 2,
                          backgroundColor: "rgba(0, 183, 244, 0.08)",
                        }),
                      }}
                      size={{
                        width: image.size.width,
                        height: image.size.height,
                      }}
                      lockAspectRatio={image.preserveAspectRatio ?? false}
                      position={{
                        x: image.position.x,
                        y: image.position.y,
                      }}
                      onDoubleClick={() => {
                        handleImageEditingMode(index);
                      }}
                      // Intentionally do not auto-open image options on drag start.
                      // Unlike text editing, image options live in a full-screen Modal
                      // (TemplateSettingsModal), which captures pointer events and blocks
                      // the underlying react-rnd resize handles while open.
                      onDragStart={() => {
                        setDraggingImageIndex(index);
                      }}
                      onDragStop={(e, d: DraggableData) => {
                        setDraggingImageIndex(null);
                        handleImageOnDragStop(index, d);
                      }}
                      onResizeStop={(
                        e,
                        direction,
                        ref: HTMLElement,
                        delta,
                        position: Position,
                      ) => handleImageResizeStop(index, ref, position)}
                    >
                      {draggingImageIndex === index && (
                        <div
                          style={{
                            position: "absolute",
                            top: -34,
                            left: "50%",
                            transform: "translateX(-50%)",
                            backgroundColor: "rgba(0, 183, 244, 0.95)",
                            color: "#fff",
                            padding: "6px 10px",
                            borderRadius: 6,
                            fontSize: 12,
                            fontFamily: "Urbanist500",
                            whiteSpace: "nowrap",
                            pointerEvents: "none",
                            zIndex: MAX_ZINDEX + 1,
                          }}
                        >
                          double click image for more options
                        </div>
                      )}
                      <img
                        src={image.src}
                        data-overlay-kind={image.kind || "standard"}
                        data-overlay-censorship-side={image.censorshipSide}
                        data-overlay-region-key={image.regionKey}
                        style={{
                          ...image.imageStyle,
                          pointerEvents: "none",
                          userSelect: "none",
                        }}
                        alt=""
                      />
                    </Rnd>
                  ),
                )}

              {template.texts &&
                template.texts.map(
                  (
                    text: {
                      content: string;
                      position: { x: number; y: number };
                      containerStyle: CSSProperties;
                      textStyle: CSSProperties;
                    },
                    index: number,
                  ) => (
                    <Rnd
                      key={index}
                      scale={editorScale}
                      bounds="parent"
                      enableResizing={false}
                      style={{
                        ...text.containerStyle,
                        ...(currentTextEditingIndex === index && {
                          zIndex: MAX_ZINDEX,
                        }),
                      }}
                      position={{
                        x: text.position.x,
                        y: text.position.y,
                      }}
                      onClick={() => {
                        if (isDraggingText.current) {
                          isDraggingText.current = false;
                          return;
                        }
                        handleTextEditingMode(index);
                      }}
                      onDoubleClick={() => {
                        handleTextEditingMode(index);
                      }}
                      onDragStart={() => {
                        isDraggingText.current = true;
                      }}
                      onDragStop={(e, d) => {
                        isDraggingText.current = false;
                        handleTextOnDragStop(index, d);
                      }}
                    >
                      <p
                        style={{
                          margin: 0,
                          ...text.textStyle,
                          ...(currentTextEditingIndex === index &&
                            !text.textStyle.borderWidth &&
                            !text.textStyle.borderTopWidth &&
                            !text.textStyle.borderLeftWidth &&
                            !text.textStyle.borderBottomWidth &&
                            !text.textStyle.borderRightWidth && {
                              border: "1px solid white",
                            }),
                          ...(currentTextEditingIndex === index && {
                            outline: "2px dashed #00b7f4",
                            outlineOffset: 2,
                            backgroundColor: "rgba(0, 183, 244, 0.08)",
                          }),
                        }}
                      >
                        {text.content}
                      </p>
                    </Rnd>
                  ),
                )}
            </div>
          </div>
        </div>
      </RainbowBorder>

      {!displayMode && (
        <>
          <RainbowBorder
            isActive={
              tipsEnabled &&
              tipsState.addTextOrImages &&
              !tipsState.continueToBeforeAfterResults
            }
            borderRadius={12}
          >
            <OpenModalButtons
              disabled={false}
              tipsEnabled={tipsEnabled}
              hasUnpairedImages={
                (swapImagesModalData?.unpairedBeforeImages?.length || 0) > 0 ||
                (swapImagesModalData?.unpairedAfterImages?.length || 0) > 0
              }
              swapImagesModalData={swapImagesModalData}
              handlePress={(value) => {
                setOpenModal(value);
                if (value === "add_text" || value === "add_image") {
                  markStepComplete(TOOLTIP_STEPS.ADD_TEXT_OR_IMAGES);
                }
              }}
            />
          </RainbowBorder>
          <TemplateSettingsModal
            visible={!!openModal}
            onClose={handleCloseEditMode}
          >
            {renderModalContent()}
          </TemplateSettingsModal>
        </>
      )}
    </div>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 15,
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 20,
  },
  buttonContainer: {
    marginTop: "auto",
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 10,
    justifyContent: "center",
  },
});

export default Template;
